import { fetchUserAttributes } from "@aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { useEffect, useRef, useState } from "react";

/* Dashboard data effects intentionally control their own fetch lifecycle. */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  listAssessmentAnswers,
  listAssessmentScores,
  listCreatePlans,
  listConnectionRequests,
  listGeneralInformations,
  listNotifications,
  listOrganizationCPs,
  listProfessionalReviews,
  listTrackPlans,
  listUserLinks,
  listUserPayments,
} from "../graphql/queries.js";
import { useConnectedClient } from "../ui-components/ConnectedClientContext.js";
const diagnosisFields = [
  "MHDxPTSD",
  "MHDxDepression",
  "MHDxSUD",
  "PHDxInfertility",
  "PHDxCurableSTD",
  "PHDxIncurableSTD",
  "PHDxCancer",
  "PHDxDiabetes",
  "PHDxHighBlood",
  "PHDxHeartDisease",
  "PHDxIrritableBowel",
  "PHDxVitA",
  "PHDxVitB",
  "PHDxVitC",
  "PHDxVitD",
  "PHDxVitE",
  "PHDxVitK",
  "PHDxAutoimmune",
  "PHDxOtherPhysical",
];
const diagnosisDisplayNames = {
  MHDxPTSD: "PTSD",
  MHDxDepression: "Depression",
  MHDxSUD: "Substance Use Disorder",
  PHDxInfertility: "Infertility",
  PHDxCurableSTD: "Curable STD",
  PHDxIncurableSTD: "Incurable STD",
  PHDxCancer: "Cancer",
  PHDxDiabetes: "Diabetes",
  PHDxHighBlood: "High Blood Pressure",
  PHDxHeartDisease: "Heart Disease",
  PHDxIrritableBowel: "Irritable Bowel Syndrome",
  PHDxVitA: "Vitamin A Deficiency",
  PHDxVitB: "Vitamin B Deficiency",
  PHDxVitC: "Vitamin C Deficiency",
  PHDxVitD: "Vitamin D Deficiency",
  PHDxVitE: "Vitamin E Deficiency",
  PHDxVitK: "Vitamin K Deficiency",
  PHDxAutoimmune: "Autoimmune Condition",
  PHDxOtherPhysical: "Other Physical Condition",
};
const traumaFields = [
  "TRHurtfulNames",
  "TRNoNurturing",
  "TRHit",
  "TRNeedsNotMet",
  "TRForcedSex",
  "TRResponsible",
  "TRViolence",
  "TRSubstanceAbuse",
  "TRMentalIllness",
  "TRParentDivorce",
  "TRParentIncarcerated",
  "TRHomelessness",
  "TRBodyFunction",
  "TRNaturalDisaster",
  "TRDirectTerrorism",
  "TRIndirectTerrorism",
  "TRLovedOne",
];

const traumaDisplayNames = {
  TRHurtfulNames: "Hurtful Names",
  TRNoNurturing: "No Nurturing",
  TRHit: "Hit",
  TRNeedsNotMet: "Needs Not Met",
  TRForcedSex: "Forced Sex",
  TRResponsible: "Responsible for Others",
  TRViolence: "Witnessed Violence",
  TRSubstanceAbuse: "Substance Abuse in Household",
  TRMentalIllness: "Mental Illness in Household",
  TRParentDivorce: "Parental Divorce",
  TRParentIncarcerated: "Parent Incarcerated",
  TRHomelessness: "Homelessness",
  TRBodyFunction: "Loss of Body Function",
  TRNaturalDisaster: "Natural Disaster",
  TRDirectTerrorism: "Direct Terrorism",
  TRIndirectTerrorism: "Indirect Terrorism",
  TRLovedOne: "Loss of Loved One",
};

const sdoHFields = [
  "SDoHAgeRange",
  "SDoHGender",
  "SDoHMaritalStatus",
  "SDoHEducation",
  "SDoHIncome",
  "SDoHJobStatus",
  "SDoHHousingStatus",
  "SDoHReligion",
];

const sdoHPieOrder = [
  "SDoHAgeRange",
  "SDoHGender",
  "SDoHMaritalStatus",
  "SDoHEducation",
  "SDoHIncome",
  "SDoHJobStatus",
  "SDoHHousingStatus",
  "SDoHReligion",
];

const sdoHDisplayNames = {
  SDoHAgeRange: "Age Range",
  SDoHGender: "Gender",
  SDoHMaritalStatus: "Marital Status",
  SDoHEducation: "Education",
  SDoHIncome: "Income",
  SDoHJobStatus: "Job Status",
  SDoHHousingStatus: "Housing Status",
  SDoHReligion: "Religion",
};
const client = generateClient();

export default function DashboardProfessional() {
  const { connectedClient, setConnectedClient } = useConnectedClient();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [reportClients, setReportClients] = useState([]);
  const [activeLinkedClientCount, setActiveLinkedClientCount] = useState(0);
  const linkIntervalsByClientIdRef = useRef(new Map());
  const [search, setSearch] = useState("");
  const [showClientWarning, setShowClientWarning] = useState(false);
  const [improvementStats, setImprovementStats] = useState({
    CM: "--",
    MH: "--",
    PH: "--",
    SH: "--",
    SoH: "--",
    OS: "--",
  });
  const [, setTopActions] = useState([]);
  const [, setTopDiagnoses] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(formatDate(oneYearAgo));
  const [endDate, setEndDate] = useState(formatDate(today));

  const [pendingStartDate, setPendingStartDate] = useState(
    formatDate(oneYearAgo),
  );
  const [pendingEndDate, setPendingEndDate] = useState(formatDate(today));

  const [, setTopTraumas] = useState([]);
  const [generalInformation, setGeneralInformation] = useState([]);
  const [generalInfoLoaded, setGeneralInfoLoaded] = useState(false);

  const [sdoHStats, setSdoHStats] = useState({
    byField: {
      SDoHAgeRange: { total: 0, counts: {} },
      SDoHGender: { total: 0, counts: {} },
      SDoHMaritalStatus: { total: 0, counts: {} },
      SDoHEducation: { total: 0, counts: {} },
      SDoHIncome: { total: 0, counts: {} },
      SDoHJobStatus: { total: 0, counts: {} },
      SDoHHousingStatus: { total: 0, counts: {} },
      SDoHReligion: { total: 0, counts: {} },
    },
  });

  // Palette: user-specified base colors + generated shades for additional categories.
  const sdoHPieBasePalette = [
    "#0067B3",
    "#8C1456",
    "#2F5D50",
    "#D4B65F",
    "#6F8F7B",
  ];

  const adjustHex = (hex, amount) => {
    const h = hex.replace("#", "");
    const num = parseInt(h, 16);
    const r = Math.max(0, Math.min(255, (num >> 16) + amount));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
    const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const buildExtendedPalette = (base) => {
    const shades = [];
    for (const c of base) {
      shades.push(c);
      shades.push(adjustHex(c, 35));
      shades.push(adjustHex(c, 70));
      shades.push(adjustHex(c, -25));
    }
    return shades;
  };

  const sdoHPiePalette = buildExtendedPalette(sdoHPieBasePalette);

  const buildPieSegments = (counts, maxSlices = 6) => {
    const entries = Object.entries(counts || {}).filter(([, c]) => c > 0);
    const total = entries.reduce((sum, [, c]) => sum + c, 0);
    if (!total) return [];

    entries.sort((a, b) => b[1] - a[1]);

    const keep = Math.max(1, Math.min(maxSlices - 1, entries.length));
    const kept = entries.slice(0, keep);
    const remainder = entries.slice(keep);
    const otherCount = remainder.reduce((sum, [, c]) => sum + c, 0);

    const segments = kept.map(([label, count], idx) => ({
      label,
      count,
      percent: (count / total) * 100,
      color: sdoHPiePalette[idx % sdoHPiePalette.length],
    }));

    if (otherCount > 0) {
      segments.push({
        label: "Other",
        count: otherCount,
        percent: (otherCount / total) * 100,
        color: sdoHPiePalette[segments.length % sdoHPiePalette.length],
      });
    }
    return segments;
  };

  const pieStyleFromSegments = (segments) => {
    if (!segments || segments.length === 0) return { background: "#f9f9f9" };

    let cursor = 0;
    const stops = segments
      .map((s) => {
        const start = cursor;
        cursor += s.percent;
        const end = cursor;
        return `${s.color} ${start.toFixed(2)}% ${end.toFixed(2)}%`;
      })
      .join(", ");

    return {
      background: `conic-gradient(${stops})`,
    };
  };

  useEffect(() => {
    const fetchGeneralInformation = async () => {
      try {
        const res = await client.graphql({
          query: listGeneralInformations,
        });
        const items = res?.data?.listGeneralInformations?.items || [];
        setGeneralInformation(items);
        setGeneralInfoLoaded(true);
      } catch (err) {
        console.error("Error fetching general information:", err);
        setGeneralInfoLoaded(true);
      }
    };

    fetchGeneralInformation();
  }, []);

  // Refetch clients when generalInformation is loaded to update emails
  // Intentionally scoped to the loaded flag to avoid duplicate fetch loops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (generalInfoLoaded) {
      fetchClients();
      fetchPlansPaidFor();
    }
  }, [generalInfoLoaded]);

  const getUserInfoById = (userId) => {
    if (!generalInformation || generalInformation.length === 0) return {};
    return (
      generalInformation.find(
        (u) => u.userId === userId || u.owner === userId,
      ) || {}
    );
  };

  const fetchTopTraumas = async (clientsList, client) => {
    try {
      const traumaCount = {};

      const range = buildSelectedDateRange();

      for (const user of clientsList) {
        const clientId = user?.clientId;
        const intervals = getLinkIntervalsForClient(clientId, user);
        const assessmentsRaw = await listAllItems({
          graphqlClient: client,
          query: listAssessmentAnswers,
          listKey: "listAssessmentAnswers",
          variables: {
            filter: {
              or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
              isActive: { eq: false },
            },
          },
        });

        const assessments = assessmentsRaw.filter((a) => {
          const ms = getAssessmentLikeMs(a);
          return (
            isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals)
          );
        });
        if (assessments.length === 0) continue;

        const latest = pickMostRecentAssessmentLike(assessments);
        if (!latest) continue;

        // Count trauma fields that are true
        for (const field of traumaFields) {
          if (latest[field] === true) {
            traumaCount[field] = (traumaCount[field] || 0) + 1;
          }
        }
      }

      const sorted = Object.entries(traumaCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      setTopTraumas(sorted);
    } catch (err) {
      console.error("Failed to fetch top traumas:", err);
      setTopTraumas([]);
    }
  };

  const buildCreatePlanLabelMap = () => {
    const entries = [
      // Strengths
      ["PSProblemSolving", "Problem-Solving Skills"],
      ["PSSelfEsteem", "Self-Esteem and Confidence"],
      ["PSChoiceAutonomy", "Choice Autonomy"],
      ["PSSelfCare", "Self-Care"],
      ["PSSelfAwareness", "Self-Awareness"],
      ["PSCreativity", "Creativity"],
      ["CSForgiveness", "Forgiveness"],
      ["CSKindness", "Kindness"],
      ["CSGratitude", "Gratitude"],
      ["CSPatience", "Patience"],
      ["CSCourage", "Courage"],
      ["CMSCopingChoice", "Coping Mechanisms"],
      ["CMSResilience", "Resilience and Perseverance"],
      ["CMSSelfRegulation", "Self-Regulation"],
      ["CMSOptimism", "Hope and Optimism"],
      ["COMSupportSystem", "Strong Support System"],
      ["COMSocialIntelligence", "Social Intelligence"],
      ["COMCommunication", "Communication Skills"],
      ["COMFaithCommunity", "Faith Based Community"],
      ["COMAccomplishments", "Accomplishments"],

      // Barriers
      ["BRStableHousing", "Access to Safe Stable Housing"],
      ["BRQualityHealthcare", "Access to Quality Healthcare"],
      ["BRProfessionalAccess", "Access to Clinical Professionals"],
      ["BRInsuranceCoverage", "Insurance Coverage"],
      ["BRFinancialConstraints", "Financial Constraints"],
      ["BREducationAttainment", "Education Attainment"],
      ["BRSocialStigma", "Social Stigma and Misconceptions"],
      ["BRUnsupportiveCommunity", "Unsupportive Personal Community"],
      ["BRNonexistentCommunity", "Nonexistent Personal Community"],
      ["BRCommunityResources", "Awareness of Community Resources"],
      ["BRProfessionalTrust", "Personal Trust in Clinical Professionals"],
      ["BRHealthcareSystemTrust", "Personal Trust in Healthcare System"],
      [
        "BRCognitiveImpairments",
        "Cognitive Impairments (e.g., memory, concentration)",
      ],
      ["BRDependentCare", "Child / Elder Care"],
      ["BRTransportation", "Transportation"],
      ["BRTechnologyAccess", "Access to Technology"],
      ["BRLanguage", "Language or Communication"],
      ["BRHealthcareProcess", "Navigating Healthcare Processes"],
      ["BROther", "Other"],

      // Triggers
      ["TGBetrayal", "Betrayal"],
      ["TGBoundaries", "Ignored Boundaries"],
      ["TGDisorder", "Loud Noise / Disorder / Chaos"],
      ["TGPain", "Pain (Emotional or Physical)"],
      ["TGRejection", "Rejection"],
      ["TGStress", "Stressful Life Events"],
      ["TGMemories", "Traumatic Memories"],
      ["TGUnjustTreatment", "Unjust Treatment"],
      [
        "TGUnpleasantConversations",
        "Unpleasant Conversations (e.g. yelling, criticism, etc.)",
      ],
      ["TGUnsafeEnvironments", "Unsafe Environments"],
      ["TGOther", "Other"],

      // Warning Signs
      ["WSFlashbacks", "Flashbacks"],
      ["WSIncreasedAppetite", "Increased Appetite"],
      ["WSDecreasedAppetite", "Decreased Appetite"],
      ["WSIntenseEmotions", "Intense Emotions / Irritability"],
      ["WSDisconnecting", "Isolation / Disconnecting"],
      ["WSTime", "Losing Track of Time"],
      ["WSNegativeSelfTalk", "Negative Self Talk"],
      ["WSNeglectingSelfCare", "Neglecting Self Care"],
      ["WSNightmares", "Nightmares"],
      ["WSRecklessBehavior", "Reckless / Destructive Behavior"],
      ["WSOther", "Other"],
    ];

    return entries.reduce((acc, [key, label]) => {
      acc[key] = label;
      return acc;
    }, {});
  };

  const fetchTop5Stats = async (clientsList, client) => {
    try {
      const createPlanLabelMap = buildCreatePlanLabelMap();
      const globalTrackPlans = [];
      const successfulActionCounts = {};
      const unsuccessfulActionCounts = {};

      const range = buildSelectedDateRange();

      const isChecked = (value) => {
        if (value === true) return true;
        if (value === 1 || value === "1") return true;
        if (typeof value === "string") {
          const v = value.trim().toLowerCase();
          return v === "true" || v === "yes" || v === "y" || v === "on";
        }
        return false;
      };

      const strengthKeys = [
        "PSProblemSolving",
        "PSSelfEsteem",
        "PSChoiceAutonomy",
        "PSSelfCare",
        "PSSelfAwareness",
        "PSCreativity",
        "CSForgiveness",
        "CSKindness",
        "CSGratitude",
        "CSPatience",
        "CSCourage",
        "CMSCopingChoice",
        "CMSResilience",
        "CMSSelfRegulation",
        "CMSOptimism",
        "COMSupportSystem",
        "COMSocialIntelligence",
        "COMCommunication",
        "COMFaithCommunity",
        "COMAccomplishments",
      ];
      const barrierKeys = [
        "BRStableHousing",
        "BRQualityHealthcare",
        "BRProfessionalAccess",
        "BRInsuranceCoverage",
        "BRFinancialConstraints",
        "BREducationAttainment",
        "BRSocialStigma",
        "BRUnsupportiveCommunity",
        "BRNonexistentCommunity",
        "BRCommunityResources",
        "BRProfessionalTrust",
        "BRHealthcareSystemTrust",
        "BRCognitiveImpairments",
        "BRDependentCare",
        "BRTransportation",
        "BRTechnologyAccess",
        "BRLanguage",
        "BRHealthcareProcess",
        "BROther",
      ];
      const triggerKeys = [
        "TGBetrayal",
        "TGBoundaries",
        "TGDisorder",
        "TGPain",
        "TGRejection",
        "TGStress",
        "TGMemories",
        "TGUnjustTreatment",
        "TGUnpleasantConversations",
        "TGUnsafeEnvironments",
        "TGOther",
      ];
      const warningSignKeys = [
        "WSFlashbacks",
        "WSIncreasedAppetite",
        "WSDecreasedAppetite",
        "WSIntenseEmotions",
        "WSDisconnecting",
        "WSTime",
        "WSNegativeSelfTalk",
        "WSNeglectingSelfCare",
        "WSNightmares",
        "WSRecklessBehavior",
        "WSOther",
      ];

      const negativeCopingFields = [
        "CMAlcohol",
        "CMDrugs",
        "CMOvereating",
        "CMSmoking",
        "CMGaming",
        "CMShopping",
        "CMGambling",
        "CMSex",
        "CMPorn",
        "CMBlaming",
        "CMHurting",
        "CMDisengage",
      ];
      const positiveCopingFields = [
        "CMArt",
        "CMMusic",
        "CMPoetry",
        "CMReading",
        "CMGroups",
        "CMCounseling",
        "CMVenting",
        "CMWriting",
        "CMSensory",
        "CMDancing",
        "CMExercising",
        "CMWalking",
        "CMChange",
        "CMAnalyze",
        "CMDaydream",
        "CMPositive",
      ];
      const copingQuestionLabels = {
        // Negative coping
        CMAlcohol: "Alcohol",
        CMDrugs: "Drugs",
        CMOvereating: "Overeating",
        CMSmoking: "Smoking",
        CMGaming: "Gaming",
        CMShopping: "Shopping",
        CMGambling: "Gambling",
        CMSex: "Casual Sex",
        CMPorn: "Pornography",
        CMBlaming: "Blaming Others",
        CMHurting: "Hurting Yourself",
        CMDisengage: "Disengaging",

        // Positive coping
        CMArt: "Art",
        CMMusic: "Music",
        CMPoetry: "Poetry",
        CMReading: "Reading",
        CMGroups: "Groups",
        CMCounseling: "Counseling",
        CMVenting: "Venting",
        CMWriting: "Writing",
        CMSensory: "Sensory Methods",
        CMDancing: "Dancing",
        CMExercising: "Exercising",
        CMWalking: "Walking",
        CMChange: "Changing Environment",
        CMAnalyze: "Analyzing Situation",
        CMDaydream: "Daydreaming",
        CMPositive: "Positive Thinking",
      };

      const mentalDxFields = ["MHDxPTSD", "MHDxDepression", "MHDxSUD"];
      const mentalDxDisplayNames = {
        MHDxPTSD: "PTSD",
        MHDxDepression: "Depression",
        MHDxSUD: "Substance Use Disorder",
      };
      const physicalDxFields = diagnosisFields.filter(
        (k) => !mentalDxFields.includes(k),
      );

      const traumaCounts = {};
      const strengthCounts = {};
      const barrierCounts = {};
      const warningSignCounts = {};
      const triggerCounts = {};
      const actionsChosenCounts = {};
      const posCopingCounts = {};
      const negCopingCounts = {};
      const outsideRangeCounts = {};
      const outsideRangeMHCounts = {};
      const outsideRangePHCounts = {};
      const outsideRangeSHCounts = {};
      const outsideRangeSoHCounts = {};
      const moreCareCounts = {};
      const physicalDxCounts = {};
      const mentalDxCounts = {};

      const scoreExcludedKeys = new Set([
        "id",
        "userId",
        "CompletedDate",
        "CompletedTime",
        "createdAt",
        "updatedAt",
        "owner",
        "__typename",
        "isActive",
        "SHSkip",
      ]);
      const isScoreQuestionKey = (key) => {
        if (scoreExcludedKeys.has(key)) return false;
        if (key.endsWith("Score")) return false;
        if (key.startsWith("SDoH")) return false;
        return true;
      };

      const formatAssessmentScoreQuestionText = (key) => {
        if (typeof key !== "string") return String(key ?? "");

        const prefixes = ["SoH", "CM", "PH", "MH", "SH"];
        const prefix = prefixes.find((p) => key.startsWith(p));
        const rest = prefix ? key.slice(prefix.length) : key;
        if (!rest) return key;

        const readable = rest
          .replace(/_/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\s+/g, " ")
          .trim();

        return readable || key;
      };

      const parseTimesPerWeek = (raw) => {
        if (raw == null) return null;
        const text = String(raw).trim().toLowerCase();
        if (!text) return null;
        if (text === "0" || text.includes("never") || text.includes("none"))
          return null;

        // Common words
        if (text.includes("daily") || text === "every day") return 7;
        if (text.includes("weekly") || text === "every week") return 1;
        if (text.includes("monthly") || text === "every month") return 1 / 4;

        // Patterns like "5x week", "5 x/week", "5 times per week"
        const unitMatch = text.match(
          /([0-9]+(?:\.[0-9]+)?)\s*(?:x|times)?\s*(?:per\s*)?(day|week|month)/,
        );
        if (unitMatch) {
          const value = Number(unitMatch[1]);
          const unit = unitMatch[2];
          if (!Number.isFinite(value) || value <= 0) return null;
          if (unit === "week") return value;
          if (unit === "day") return value * 7;
          if (unit === "month") return value / 4;
        }

        // If user enters a bare number, assume times per week
        const numberOnly = Number(text.replace(/[^0-9.]/g, ""));
        if (Number.isFinite(numberOnly) && numberOnly > 0) return numberOnly;

        return null;
      };

      const pickMostRecent = (items, getMs) => {
        const sorted = [...items].sort((a, b) => {
          const dA = getMs(a) ?? 0;
          const dB = getMs(b) ?? 0;
          return dB - dA;
        });
        return sorted[0] || null;
      };

      const uniqueClientIds = new Set();
      const uniqueClientsList = [];
      for (const user of clientsList || []) {
        const id = user?.clientId;
        if (!id || uniqueClientIds.has(id)) continue;
        uniqueClientIds.add(id);
        uniqueClientsList.push(user);
      }

      const totalUnduplicatedClients = uniqueClientsList.length;

      for (const user of uniqueClientsList) {
        const clientId = user?.clientId;
        if (!clientId) continue;

        const intervals = getLinkIntervalsForClient(clientId, user);

        let assessmentsRaw = [];
        let scoresRaw = [];
        let trackPlansRaw = [];
        let createPlansRaw = [];
        try {
          [assessmentsRaw, scoresRaw, trackPlansRaw, createPlansRaw] =
            await Promise.all([
              listAllItems({
                graphqlClient: client,
                query: listAssessmentAnswers,
                listKey: "listAssessmentAnswers",
                authMode: "userPool",
                variables: {
                  filter: {
                    and: [
                      {
                        or: [
                          { owner: { eq: clientId } },
                          { userId: { eq: clientId } },
                        ],
                      },
                      {
                        or: [
                          { CompletedDate: { attributeExists: true } },
                          { isActive: { eq: false } },
                        ],
                      },
                    ],
                  },
                },
              }),
              listAllItems({
                graphqlClient: client,
                query: listAssessmentScores,
                listKey: "listAssessmentScores",
                authMode: "userPool",
                variables: {
                  filter: {
                    and: [
                      {
                        or: [
                          { owner: { eq: clientId } },
                          { userId: { eq: clientId } },
                        ],
                      },
                      {
                        or: [
                          { CompletedDate: { attributeExists: true } },
                          { isActive: { eq: false } },
                        ],
                      },
                    ],
                  },
                },
              }),
              listAllItems({
                graphqlClient: client,
                query: listTrackPlans,
                listKey: "listTrackPlans",
                authMode: "userPool",
                variables: {
                  filter: {
                    or: [
                      { owner: { eq: clientId } },
                      { userId: { eq: clientId } },
                    ],
                  },
                },
              }),
              listAllItems({
                graphqlClient: client,
                query: listCreatePlans,
                listKey: "listCreatePlans",
                authMode: "userPool",
                variables: {
                  filter: {
                    userId: { eq: clientId },
                  },
                },
              }),
            ]);
        } catch (err) {
          console.warn(
            "Top 5 stats: unable to load data for client",
            clientId,
            err,
          );
          if (Array.isArray(err?.errors) && err.errors.length) {
            console.warn(
              "Top 5 stats per-client GraphQL errors:",
              err.errors.map((e) => e?.message || e),
            );
          }
          continue;
        }

        const allowedByWindow = (ms) =>
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals);

        const assessments = assessmentsRaw.filter((a) =>
          allowedByWindow(getAssessmentLikeMs(a)),
        );
        const scores = scoresRaw.filter((s) =>
          allowedByWindow(getAssessmentLikeMs(s)),
        );
        const trackPlans = trackPlansRaw.filter((p) =>
          allowedByWindow(toMs(p?.createdAt)),
        );
        globalTrackPlans.push(...trackPlans);
        const createPlans = createPlansRaw.filter((p) =>
          allowedByWindow(toMs(p?.createdAt)),
        );

        const latestAssessment = assessments.length
          ? pickMostRecentAssessmentLike(assessments)
          : null;
        const latestScores = scores.length
          ? pickMostRecentAssessmentLike(scores)
          : null;
        const latestCreatePlan = createPlans.length
          ? pickMostRecent(createPlans, (x) => toMs(x?.createdAt))
          : null;

        if (latestAssessment) {
          for (const field of traumaFields) {
            if (latestAssessment?.[field] === true) {
              const label = traumaDisplayNames[field] || field;
              traumaCounts[label] = (traumaCounts[label] || 0) + 1;
            }
          }

          // Coping Mechanisms are stored as frequency strings (e.g. "5x week").
          for (const field of positiveCopingFields) {
            const timesPerWeek = parseTimesPerWeek(latestAssessment?.[field]);
            if (!timesPerWeek) continue;
            const label = copingQuestionLabels[field] || field;
            posCopingCounts[label] = (posCopingCounts[label] || 0) + 1;
          }
          for (const field of negativeCopingFields) {
            const timesPerWeek = parseTimesPerWeek(latestAssessment?.[field]);
            if (!timesPerWeek) continue;
            const label = copingQuestionLabels[field] || field;
            negCopingCounts[label] = (negCopingCounts[label] || 0) + 1;
          }

          for (const field of physicalDxFields) {
            if (latestAssessment?.[field]) {
              const label = diagnosisDisplayNames[field] || field;
              physicalDxCounts[label] = (physicalDxCounts[label] || 0) + 1;
            }
          }
          for (const field of mentalDxFields) {
            if (latestAssessment?.[field]) {
              const label = mentalDxDisplayNames[field] || field;
              mentalDxCounts[label] = (mentalDxCounts[label] || 0) + 1;
            }
          }
        }

        if (latestScores) {
          for (const [key, raw] of Object.entries(latestScores)) {
            if (!isScoreQuestionKey(key)) continue;
            const num = typeof raw === "number" ? raw : Number(raw);
            const label = formatAssessmentScoreQuestionText(key);
            if (num === 3 || num === 4) {
              outsideRangeCounts[label] = (outsideRangeCounts[label] || 0) + 1;
              if (key.startsWith("MH")) {
                outsideRangeMHCounts[label] =
                  (outsideRangeMHCounts[label] || 0) + 1;
              } else if (key.startsWith("PH")) {
                outsideRangePHCounts[label] =
                  (outsideRangePHCounts[label] || 0) + 1;
              } else if (key.startsWith("SH")) {
                outsideRangeSHCounts[label] =
                  (outsideRangeSHCounts[label] || 0) + 1;
              } else if (key.startsWith("SoH")) {
                outsideRangeSoHCounts[label] =
                  (outsideRangeSoHCounts[label] || 0) + 1;
              }
            } else if (num === 2) {
              moreCareCounts[label] = (moreCareCounts[label] || 0) + 1;
            }
          }
        }

        if (trackPlans.length) {
          const latestPlanMs = latestCreatePlan?.createdAt
            ? new Date(latestCreatePlan.createdAt).getTime()
            : null;
          const filteredByLatestPlan = latestPlanMs
            ? trackPlans.filter((p) => {
                const ms = p?.createdAt ? new Date(p.createdAt).getTime() : 0;
                return ms >= latestPlanMs;
              })
            : trackPlans;

          const relevantTrackPlans = filteredByLatestPlan.length
            ? filteredByLatestPlan
            : trackPlans;

          const uniqueActions = new Set();
          for (const plan of relevantTrackPlans) {
            const action = plan?.Action ? String(plan.Action).trim() : "";
            if (action) uniqueActions.add(action);
          }
          for (const action of uniqueActions) {
            actionsChosenCounts[action] =
              (actionsChosenCounts[action] || 0) + 1;
          }

          const successfulActionsForClient = new Set();
          const unsuccessfulActionsForClient = new Set();
          for (const plan of relevantTrackPlans) {
            const action = plan?.Action ? String(plan.Action).trim() : "";
            if (!action) continue;
            if (plan?.Successful) successfulActionsForClient.add(action);
            if (plan?.Discontinued) unsuccessfulActionsForClient.add(action);
          }
          for (const action of successfulActionsForClient) {
            successfulActionCounts[action] =
              (successfulActionCounts[action] || 0) + 1;
          }
          for (const action of unsuccessfulActionsForClient) {
            unsuccessfulActionCounts[action] =
              (unsuccessfulActionCounts[action] || 0) + 1;
          }
        }

        if (latestCreatePlan) {
          for (const key of strengthKeys) {
            if (isChecked(latestCreatePlan?.[key])) {
              const label = createPlanLabelMap[key] || key;
              strengthCounts[label] = (strengthCounts[label] || 0) + 1;
            }
          }
          for (const key of barrierKeys) {
            if (isChecked(latestCreatePlan?.[key])) {
              const label = createPlanLabelMap[key] || key;
              barrierCounts[label] = (barrierCounts[label] || 0) + 1;
            }
          }
          for (const key of warningSignKeys) {
            if (isChecked(latestCreatePlan?.[key])) {
              const label = createPlanLabelMap[key] || key;
              warningSignCounts[label] = (warningSignCounts[label] || 0) + 1;
            }
          }
          for (const key of triggerKeys) {
            if (isChecked(latestCreatePlan?.[key])) {
              const label = createPlanLabelMap[key] || key;
              triggerCounts[label] = (triggerCounts[label] || 0) + 1;
            }
          }
        }
      }

      const toTop5Rate = (hits, denominator) =>
        Object.entries(hits)
          .map(([label, count]) => {
            const rate = denominator > 0 ? count / denominator : 0;
            return { label, count: rate };
          })
          .filter((x) => Number.isFinite(x.count) && x.count > 0)
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

      setTop5Stats({
        TRAUMAS: toTop5Rate(traumaCounts, totalUnduplicatedClients),
        STRENGTHS: toTop5Rate(strengthCounts, totalUnduplicatedClients),
        BARRIERS: toTop5Rate(barrierCounts, totalUnduplicatedClients),
        WARNING_SIGNS: toTop5Rate(warningSignCounts, totalUnduplicatedClients),
        TRIGGERS: toTop5Rate(triggerCounts, totalUnduplicatedClients),
        ACTIONS_CHOSEN: toTop5Rate(
          actionsChosenCounts,
          totalUnduplicatedClients,
        ),
        SUCCESSFUL_ACTIONS: toTop5Rate(
          successfulActionCounts,
          totalUnduplicatedClients,
        ),
        UNSUCCESSFUL_ACTIONS: toTop5Rate(
          unsuccessfulActionCounts,
          totalUnduplicatedClients,
        ),
        POS_COPING: toTop5Rate(posCopingCounts, totalUnduplicatedClients),
        NEG_COPING: toTop5Rate(negCopingCounts, totalUnduplicatedClients),
        OUTSIDE_RANGE_MH: toTop5Rate(
          outsideRangeMHCounts,
          totalUnduplicatedClients,
        ),
        OUTSIDE_RANGE_PH: toTop5Rate(
          outsideRangePHCounts,
          totalUnduplicatedClients,
        ),
        OUTSIDE_RANGE_SH: toTop5Rate(
          outsideRangeSHCounts,
          totalUnduplicatedClients,
        ),
        OUTSIDE_RANGE_SOH: toTop5Rate(
          outsideRangeSoHCounts,
          totalUnduplicatedClients,
        ),
        PHYSICAL_DX: toTop5Rate(physicalDxCounts, totalUnduplicatedClients),
        MENTAL_DX: toTop5Rate(mentalDxCounts, totalUnduplicatedClients),
      });
    } catch (err) {
      console.error("Failed to fetch Top 5 stats:", err);
      if (Array.isArray(err?.errors) && err.errors.length) {
        console.error(
          "Top 5 stats GraphQL errors:",
          err.errors.map((e) => e?.message || e),
        );
      }
      setTop5Stats({
        TRAUMAS: [],
        STRENGTHS: [],
        BARRIERS: [],
        WARNING_SIGNS: [],
        TRIGGERS: [],
        ACTIONS_CHOSEN: [],
        POS_COPING: [],
        NEG_COPING: [],
        OUTSIDE_RANGE_MH: [],
        OUTSIDE_RANGE_PH: [],
        OUTSIDE_RANGE_SH: [],
        OUTSIDE_RANGE_SOH: [],
        PHYSICAL_DX: [],
        MENTAL_DX: [],
      });
    }
  };
  useEffect(() => {
    // Stats + Top 5 should respond to search/date filters.
    // If a specific client is selected, scope to them even if filters would exclude them.
    const matchingReportClients = reportClients.filter((reportClient) => {
      const first = reportClient.firstName?.toLowerCase() || "";
      const last = reportClient.lastName?.toLowerCase() || "";
      const email = reportClient.email?.toLowerCase() || "";
      const fullName = `${first} ${last}`;
      const searchTerms = search.toLowerCase().trim().split(/\s+/);
      const matchesSearch = searchTerms.every(
        (term) =>
          first.includes(term) ||
          last.includes(term) ||
          email.includes(term) ||
          fullName.includes(term),
      );
      const intervals = getLinkIntervalsForClient(
        reportClient.clientId,
        reportClient,
      );
      return (
        matchesSearch &&
        intervals.some((interval) =>
          intervalOverlapsRange(interval, buildSelectedDateRange()),
        )
      );
    });

    const scopedClients = connectedClient?.clientId
      ? filteredClients.filter((c) => c.clientId === connectedClient.clientId)
      : filteredClients;

    const statsSource =
      connectedClient?.clientId && scopedClients.length === 0
        ? clients.filter((c) => c.clientId === connectedClient.clientId)
        : connectedClient?.clientId
          ? scopedClients
          : matchingReportClients;

    const loadStats = async () => {
      if (statsSource.length > 0) {
        await fetchCombinedStats(statsSource);
        await calculateImprovementStats(statsSource, client);
        await fetchTop5Stats(statsSource, client);
        await fetchSdoHStats(statsSource, client);
      } else {
        setCombinedStats({
          totalAssessments: 0,
          unduplicatedAssessments: 0,
          totalInProgress: 0,
          totalDiscontinued: 0,
          totalSuccessful: 0,
          assessmentsReviewed: 0,
          plansReviewed: 0,
        });
        setImprovementStats({
          CM: "--",
          MH: "--",
          PH: "--",
          SH: "--",
          SoH: "--",
          OS: "--",
        });
        setTop5Stats({
          TRAUMAS: [],
          STRENGTHS: [],
          BARRIERS: [],
          WARNING_SIGNS: [],
          TRIGGERS: [],
          ACTIONS_CHOSEN: [],
          POS_COPING: [],
          NEG_COPING: [],
          OUTSIDE_RANGE: [],
          MORE_CARE: [],
          PHYSICAL_DX: [],
          MENTAL_DX: [],
        });
        setSdoHStats({
          byField: {
            SDoHAgeRange: { total: 0, counts: {} },
            SDoHGender: { total: 0, counts: {} },
            SDoHMaritalStatus: { total: 0, counts: {} },
            SDoHEducation: { total: 0, counts: {} },
            SDoHIncome: { total: 0, counts: {} },
            SDoHJobStatus: { total: 0, counts: {} },
            SDoHHousingStatus: { total: 0, counts: {} },
            SDoHReligion: { total: 0, counts: {} },
          },
        });
      }
    };

    loadStats();
  }, [
    clients,
    filteredClients,
    reportClients,
    connectedClient,
    client,
    search,
    startDate,
    endDate,
  ]);

  const fetchSdoHStats = async (clientsList, client) => {
    if (!client || typeof client.graphql !== "function") {
      setSdoHStats({
        byField: {
          SDoHAgeRange: { total: 0, counts: {} },
          SDoHGender: { total: 0, counts: {} },
          SDoHMaritalStatus: { total: 0, counts: {} },
          SDoHEducation: { total: 0, counts: {} },
          SDoHIncome: { total: 0, counts: {} },
          SDoHJobStatus: { total: 0, counts: {} },
          SDoHHousingStatus: { total: 0, counts: {} },
          SDoHReligion: { total: 0, counts: {} },
        },
      });
      return;
    }

    try {
      const valueCountsByField = Object.fromEntries(
        sdoHFields.map((f) => [f, {}]),
      );
      const totalsByField = Object.fromEntries(sdoHFields.map((f) => [f, 0]));

      const range = buildSelectedDateRange();

      const uniqueClientIds = new Set();
      const uniqueClientsList = [];
      for (const user of clientsList || []) {
        const id = user?.clientId;
        if (!id || uniqueClientIds.has(id)) continue;
        uniqueClientIds.add(id);
        uniqueClientsList.push(user);
      }

      for (const user of uniqueClientsList) {
        const clientId = user?.clientId;
        if (!clientId) continue;
        const intervals = getLinkIntervalsForClient(clientId, user);
        const assessmentsRaw = await listAllItems({
          graphqlClient: client,
          query: listAssessmentAnswers,
          listKey: "listAssessmentAnswers",
          authMode: "userPool",
          variables: {
            filter: {
              and: [
                {
                  or: [
                    { owner: { eq: clientId } },
                    { userId: { eq: clientId } },
                  ],
                },
                {
                  or: [
                    { CompletedDate: { attributeExists: true } },
                    { isActive: { eq: false } },
                  ],
                },
              ],
            },
          },
        });

        const assessments = assessmentsRaw.filter((a) => {
          const ms = getAssessmentLikeMs(a);
          return (
            isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals)
          );
        });
        if (assessments.length === 0) continue;

        const latest = pickMostRecentAssessmentLike(assessments);
        if (!latest) continue;
        for (const field of sdoHFields) {
          const raw = latest?.[field];
          const value = typeof raw === "string" ? raw.trim() : "";
          if (!value) continue;
          totalsByField[field] += 1;
          valueCountsByField[field][value] =
            (valueCountsByField[field][value] || 0) + 1;
        }
      }

      const byField = {};
      for (const field of sdoHFields) {
        byField[field] = {
          total: totalsByField[field] || 0,
          counts: valueCountsByField[field] || {},
        };
      }

      setSdoHStats({ byField });
    } catch (err) {
      console.error("Failed to fetch SDoH stats:", err);
      setSdoHStats({
        byField: {
          SDoHAgeRange: { total: 0, counts: {} },
          SDoHGender: { total: 0, counts: {} },
          SDoHMaritalStatus: { total: 0, counts: {} },
          SDoHEducation: { total: 0, counts: {} },
          SDoHIncome: { total: 0, counts: {} },
          SDoHJobStatus: { total: 0, counts: {} },
          SDoHHousingStatus: { total: 0, counts: {} },
          SDoHReligion: { total: 0, counts: {} },
        },
      });
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const containerStyle2 = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: isMobile ? "90%" : "1000px",
    margin: "1px auto",
    marginLeft: "auto",
    marginRight: "auto",
    padding: 24,
    borderRadius: 12,
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };
  const statsContainerResponsive = {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: isMobile ? "center" : "flex-start",
    width: isMobile ? "90%" : "1000px",
    margin: "24px auto",
    gap: isMobile ? 12 : 24,
    flexDirection: isMobile ? "column" : "row",
  };
  const sideBoxStyle = {
    flex: "1",
    padding: 20,
    width: isMobile ? "100%" : "auto",
    borderRadius: 12,
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    minHeight: 350,
    alignSelf: "flex-start",
  };

  const [actionStats, setActionStats] = useState({
    inProgress: 0,
    discontinued: 0,
    successful: 0,
  });
  const [combinedStats, setCombinedStats] = useState({
    totalAssessments: 0,
    unduplicatedAssessments: 0,
    totalInProgress: 0,
    totalDiscontinued: 0,
    totalSuccessful: 0,
    assessmentsReviewed: 0,
    plansReviewed: 0,
  });
  const [connectedReviewStats, setConnectedReviewStats] = useState({
    assessmentsReviewed: 0,
    plansReviewed: 0,
  });
  const [recommendationStats, setRecommendationStats] = useState({
    sent: 0,
    accepted: 0,
  });
  const fetchTopActions = async (clientsList, client) => {
    try {
      const actionCount = {};

      const range = buildSelectedDateRange();

      for (const user of clientsList) {
        const clientId = user?.clientId;
        if (!clientId) continue;
        const intervals = getLinkIntervalsForClient(clientId, user);
        const plansRaw = await listAllItems({
          graphqlClient: client,
          query: listTrackPlans,
          listKey: "listTrackPlans",
          variables: {
            filter: {
              or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
            },
          },
        });

        const plans = plansRaw.filter((p) => {
          const ms = toMs(p?.createdAt);
          return (
            isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals)
          );
        });

        for (const plan of plans) {
          const action = plan?.Action?.trim();
          if (action) {
            actionCount[action] = (actionCount[action] || 0) + 1;
          }
        }
      }

      const sorted = Object.entries(actionCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      setTopActions(sorted);
    } catch (err) {
      console.error("Failed to fetch top actions:", err);
      setTopActions([]);
    }
  };

  const fetchTopDiagnoses = async (clientsList, client) => {
    try {
      const diagnosisCount = {};

      const range = buildSelectedDateRange();

      for (const user of clientsList) {
        const clientId = user?.clientId;
        if (!clientId) continue;
        const intervals = getLinkIntervalsForClient(clientId, user);
        const assessmentsRaw = await listAllItems({
          graphqlClient: client,
          query: listAssessmentAnswers,
          listKey: "listAssessmentAnswers",
          variables: {
            filter: {
              or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
              isActive: { eq: false },
            },
          },
        });

        const assessments = assessmentsRaw.filter((a) => {
          const ms = getAssessmentLikeMs(a);
          return (
            isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals)
          );
        });
        if (assessments.length === 0) continue;

        const latest = pickMostRecentAssessmentLike(assessments);
        if (!latest) continue;

        for (const field of diagnosisFields) {
          if (latest[field]) {
            diagnosisCount[field] = (diagnosisCount[field] || 0) + 1;
          }
        }
      }

      const sorted = Object.entries(diagnosisCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      setTopDiagnoses(sorted);
    } catch (err) {
      console.error("Failed to fetch top diagnoses:", err);
      setTopDiagnoses([]);
    }
  };

  const fetchRecommendationStats = async () => {
    try {
      const attrs = await fetchUserAttributes();
      const userId = attrs.sub;
      const userEmail = String(attrs.email || "")
        .trim()
        .toLowerCase();

      const range = buildSelectedDateRange();
      const { data } = await client.graphql({
        query: listNotifications,
        variables: { limit: 200 },
      });

      const notifications = data?.listNotifications?.items || [];

      const notificationsInRange = notifications.filter((n) =>
        isMsWithinRange(toMs(n?.createdAt), range),
      );

      // If a specific client is selected, filter by their email
      const targetClientEmail = connectedClient?.email;

      const sent = notificationsInRange.filter(
        (n) =>
          n.NotificationType === "Recommendation" &&
          n.SenderId === userId &&
          (!targetClientEmail || n.TargetEmail === targetClientEmail),
      ).length;

      const accepted = notificationsInRange.filter(
        (n) =>
          n.NotificationType === "RecommendationBack" &&
          n.TargetEmail === userEmail &&
          n.RecommendationNote === "Accepted" &&
          (!targetClientEmail || n.SenderEmail === targetClientEmail),
      ).length;

      setRecommendationStats({ sent, accepted });
    } catch (err) {
      console.error("Failed to load recommendation stats:", err);
      setRecommendationStats({ sent: 0, accepted: 0 });
    }
  };
  const fetchPlansPaidFor = async () => {
    try {
      const attrs = await fetchUserAttributes();
      const userId = attrs.sub;
      const today = new Date().toISOString();

      // Step 1: Check if user is in an organization
      const orgRes = await client.graphql({
        query: listOrganizationCPs,
        variables: {
          filter: {
            OrganizationCPEmail: { eq: attrs.email },
          },
        },
      });

      const orgLink = orgRes?.data?.listOrganizationCPs?.items?.[0];
      const orgId = orgLink?.OrganizationId;

      // Step 2: Construct full filter using AND
      const filterConditions = [
        { paidUntil: { gt: today } },
        {
          or: [
            { paymentStatus: { eq: "active" } },
            { paymentStatus: { eq: "completed" } },
          ],
        },
        orgId ? { organizationId: { eq: orgId } } : { userId: { eq: userId } },
      ];

      const { data } = await client.graphql({
        query: listUserPayments,
        variables: {
          filter: {
            and: filterConditions,
          },
        },
      });

      const payments = data?.listUserPayments?.items || [];

      const totalPlans = payments.reduce((sum, payment) => {
        return sum + (Number(payment.clientsProjected) || 0);
      }, 0);

      setPlansPaidFor(totalPlans);
    } catch (err) {
      console.error("Failed to fetch plans paid for:", err);
      setPlansPaidFor(0);
    }
  };

  function parseInputDate(dateStr) {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-based
  }

  function buildSelectedDateRange() {
    const start = startDate ? parseInputDate(startDate) : null;
    const end = endDate ? parseInputDate(endDate) : null;
    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);
    return {
      startMs: start ? start.getTime() : null,
      endMs: end ? end.getTime() : null,
    };
  }

  function toMs(value) {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  async function listAllItems({
    graphqlClient,
    query,
    variables,
    listKey,
    authMode,
    pageLimit = 1000,
  }) {
    if (!graphqlClient || typeof graphqlClient.graphql !== "function") {
      throw new Error("Invalid graphqlClient passed to listAllItems");
    }
    if (!listKey) throw new Error("listKey is required");

    const allItems = [];
    let nextToken = null;

    do {
      const res = await graphqlClient.graphql({
        query,
        variables: {
          ...(variables || {}),
          limit: (variables && variables.limit) || pageLimit,
          nextToken,
        },
        ...(authMode ? { authMode } : {}),
      });

      const connection = res?.data?.[listKey];
      const items = connection?.items || [];
      if (Array.isArray(items) && items.length) allItems.push(...items);
      nextToken = connection?.nextToken || null;
    } while (nextToken);

    return allItems;
  }

  function intervalOverlapsRange(interval, range) {
    if (!interval) return false;
    const startMs = toMs(interval.start);
    if (!startMs) return false;
    const endMs = interval.end ? toMs(interval.end) : null;

    const rangeStart = range?.startMs ?? null;
    const rangeEnd = range?.endMs ?? null;

    const effectiveEnd = endMs ?? Number.POSITIVE_INFINITY;
    const effectiveRangeStart = rangeStart ?? Number.NEGATIVE_INFINITY;
    const effectiveRangeEnd = rangeEnd ?? Number.POSITIVE_INFINITY;

    return startMs <= effectiveRangeEnd && effectiveEnd >= effectiveRangeStart;
  }

  function isMsWithinRange(ms, range) {
    if (!Number.isFinite(ms)) return false;
    if (!range) return true;
    if (range.startMs != null && ms < range.startMs) return false;
    if (range.endMs != null && ms > range.endMs) return false;
    return true;
  }

  function isMsWithinAnyInterval(ms, intervals) {
    if (!Number.isFinite(ms)) return false;
    if (!Array.isArray(intervals) || intervals.length === 0) return true;
    for (const i of intervals) {
      const startMs = toMs(i?.start);
      if (!startMs) continue;
      const endMs = i?.end ? toMs(i.end) : null;
      const effectiveEnd = endMs ?? Number.POSITIVE_INFINITY;
      if (ms >= startMs && ms <= effectiveEnd) return true;
    }
    return false;
  }

  function getLinkIntervalsForClient(clientId, fallbackClient) {
    if (!clientId) return [];
    const map = linkIntervalsByClientIdRef.current;
    const existing = map?.get?.(clientId);
    if (Array.isArray(existing) && existing.length > 0) return existing;
    const start = fallbackClient?.linkedAt || fallbackClient?.createdAt || null;
    const end = fallbackClient?.unlinkedAt || null;
    return start ? [{ start, end }] : [];
  }

  function parseCompletedDateTimeMs(completedDateRaw, completedTimeRaw) {
    const date = String(completedDateRaw || "").trim();
    if (!date) return null;

    // Some deployments store a full ISO datetime in CompletedDate (schema drift).
    // If so, prefer parsing it directly.
    if (/^\d{4}-\d{2}-\d{2}T/.test(date)) {
      const direct = toMs(date);
      if (direct != null) return direct;
    }

    let normalizedDate = "";

    // Accept YYYY-MM-DD or MM/DD/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      normalizedDate = date;
    } else {
      const mdy = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mdy) {
        const mm = String(mdy[1]).padStart(2, "0");
        const dd = String(mdy[2]).padStart(2, "0");
        const yyyy = mdy[3];
        normalizedDate = `${yyyy}-${mm}-${dd}`;
      }
    }
    if (!normalizedDate) return null;

    const time = String(completedTimeRaw || "").trim();
    let normalizedTime = "";
    let tzSuffix = "";
    // Accept HH:MM[:SS] (24h)
    const timeWithTz = time.match(
      /^(\d{1,2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)(Z|[+-]\d{2}:?\d{2})$/i,
    );
    if (timeWithTz) {
      normalizedTime = timeWithTz[1];
      tzSuffix = timeWithTz[2].toUpperCase();
      if (/^\d{1,2}:\d{2}$/.test(normalizedTime)) {
        normalizedTime = `${normalizedTime}:00`;
      }
      // Normalize offsets like +0000 to +00:00
      if (/^[+-]\d{4}$/.test(tzSuffix)) {
        tzSuffix = `${tzSuffix.slice(0, 3)}:${tzSuffix.slice(3)}`;
      }
    } else if (time && /^\d{1,2}:\d{2}(:\d{2}(?:\.\d{1,3})?)?$/.test(time)) {
      // HH:MM
      if (/^\d{1,2}:\d{2}$/.test(time)) {
        normalizedTime = `${time}:00`;
      } else {
        // HH:MM:SS or HH:MM:SS.mmm
        normalizedTime = time;
      }
    } else if (time) {
      // Accept h:mm[:ss] AM/PM
      const ampm = time.match(
        /^\s*(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?\s*(AM|PM)\s*$/i,
      );
      if (ampm) {
        let hours = Number(ampm[1]);
        const minutes = Number(ampm[2]);
        const seconds = Number(ampm[3] || 0);
        const millisRaw = ampm[4];
        const meridiem = String(ampm[5]).toUpperCase();
        if (meridiem === "PM" && hours < 12) hours += 12;
        if (meridiem === "AM" && hours === 12) hours = 0;
        const base = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        if (millisRaw) {
          const millis = String(millisRaw).padEnd(3, "0").slice(0, 3);
          normalizedTime = `${base}.${millis}`;
        } else {
          normalizedTime = base;
        }
      }
    }

    const iso = normalizedTime
      ? `${normalizedDate}T${normalizedTime}${tzSuffix}`
      : `${normalizedDate}T00:00:00`;

    const ms = new Date(iso).getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  function getAssessmentLikeMs(item) {
    const completedMs = parseCompletedDateTimeMs(
      item?.CompletedDate,
      item?.CompletedTime,
    );
    if (completedMs != null) return completedMs;
    return toMs(item?.updatedAt) ?? toMs(item?.createdAt) ?? null;
  }

  function compareAssessmentLikeDesc(a, b) {
    const aPrimary = getAssessmentLikeMs(a) ?? 0;
    const bPrimary = getAssessmentLikeMs(b) ?? 0;
    if (aPrimary !== bPrimary) return bPrimary - aPrimary;

    const aUpdated = toMs(a?.updatedAt) ?? 0;
    const bUpdated = toMs(b?.updatedAt) ?? 0;
    if (aUpdated !== bUpdated) return bUpdated - aUpdated;

    const aCreated = toMs(a?.createdAt) ?? 0;
    const bCreated = toMs(b?.createdAt) ?? 0;
    if (aCreated !== bCreated) return bCreated - aCreated;

    return String(b?.id || "").localeCompare(String(a?.id || ""));
  }

  function pickMostRecentAssessmentLike(items) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return [...items].sort(compareAssessmentLikeDesc)[0] || null;
  }
  const [pendingRequests, setPendingRequests] = useState(0);
  const [, setTotalRequests] = useState(0);
  const [completedAssessments, setCompletedAssessments] = useState(0);
  const TOP5_SECTIONS = [
    { key: "STRENGTHS", label: "Strengths" },
    { key: "BARRIERS", label: "Barriers" },
    { key: "WARNING_SIGNS", label: "Warning Signs" },
    { key: "TRIGGERS", label: "Triggers" },
    { key: "POS_COPING", label: "Positive Coping" },
    { key: "NEG_COPING", label: "Negative Coping" },
    { key: "PHYSICAL_DX", label: "Physical Diagnoses" },
    { key: "MENTAL_DX", label: "Mental Diagnoses" },
    { key: "OUTSIDE_RANGE_MH", label: "Outside a Healthy Range - MH" },
    { key: "OUTSIDE_RANGE_PH", label: "Outside a Healthy Range - PH" },
    { key: "OUTSIDE_RANGE_SH", label: "Outside a Healthy Range - SH" },
    { key: "OUTSIDE_RANGE_SOH", label: "Outside a Healthy Range - SoH" },
    { key: "TRAUMAS", label: "Traumas" },
    { key: "ACTIONS_CHOSEN", label: "Actions Chosen" },
    { key: "SUCCESSFUL_ACTIONS", label: "Successful Actions" },
    { key: "UNSUCCESSFUL_ACTIONS", label: "Unsuccessful Actions" },
  ];

  const [openTop5Sections, setOpenTop5Sections] = useState({});
  const [top5Stats, setTop5Stats] = useState({
    TRAUMAS: [],
    STRENGTHS: [],
    BARRIERS: [],
    WARNING_SIGNS: [],
    TRIGGERS: [],
    ACTIONS_CHOSEN: [],
    SUCCESSFUL_ACTIONS: [],
    UNSUCCESSFUL_ACTIONS: [],
    POS_COPING: [],
    NEG_COPING: [],
    OUTSIDE_RANGE_MH: [],
    OUTSIDE_RANGE_PH: [],
    OUTSIDE_RANGE_SH: [],
    OUTSIDE_RANGE_SOH: [],
    PHYSICAL_DX: [],
    MENTAL_DX: [],
  });

  const toggleTop5Section = (key) => {
    setOpenTop5Sections((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  const formatTop5Count = (sectionKey, value) => {
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num) || num <= 0) return "--";
    const pct = Math.round(num * 1000) / 10;
    const pretty = Number.isInteger(pct) ? String(pct) : String(pct);
    return `${pretty}%`;
  };
  const [plansPaidFor, setPlansPaidFor] = useState(0);

  useEffect(() => {
    fetchConnectionRequestStats();
  }, []);

  // Intentionally keyed to date/search primitives used by this filter pass.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const filtered = clients
      .filter((client) => {
        // Search filter
        const first = client.firstName?.toLowerCase() || "";
        const last = client.lastName?.toLowerCase() || "";
        const email = client.email?.toLowerCase() || "";
        const fullName = `${first} ${last}`;
        const searchTerms = search.toLowerCase().trim().split(/\s+/);

        return searchTerms.every(
          (term) =>
            first.includes(term) ||
            last.includes(term) ||
            email.includes(term) ||
            fullName.includes(term),
        );
      })
      .filter((client) => {
        // Date filter: include clients whose link interval overlaps the selected range.
        const range = buildSelectedDateRange();
        if (range.startMs == null && range.endMs == null) return true;

        const intervals = getLinkIntervalsForClient(client?.clientId, client);
        if (!intervals || intervals.length === 0) return false;
        return intervals.some((i) => intervalOverlapsRange(i, range));
      });

    setFilteredClients(filtered);
  }, [clients, search, startDate, endDate]);
  // Intentionally refresh recommendation stats when selected client context changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRecommendationStats();
  }, [connectedClient]);
  // Intentionally keyed to client selection and combined summary reset behavior.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (connectedClient) {
      fetchAssessmentStats(connectedClient.clientId);
      fetchActionStats(connectedClient.clientId); // ✅
      fetchReviewStatsForClient(connectedClient.clientId);
    } else {
      // Reset to combined stats when no client is selected
      setActionStats({
        inProgress: combinedStats.totalInProgress,
        discontinued: combinedStats.totalDiscontinued,
        successful: combinedStats.totalSuccessful,
      });
      setCompletedAssessments(combinedStats.totalAssessments);
      setConnectedReviewStats({
        assessmentsReviewed: combinedStats.assessmentsReviewed,
        plansReviewed: combinedStats.plansReviewed,
      });
    }
  }, [connectedClient, combinedStats]);

  const fetchReviewStatsForClient = async (clientId) => {
    try {
      const range = buildSelectedDateRange();
      const intervals = getLinkIntervalsForClient(clientId, null);

      const allReviews = [];
      try {
        let nextToken = null;
        do {
          const res = await client.graphql({
            query: listProfessionalReviews,
            variables: { limit: 1000, nextToken },
            authMode: "userPool",
          });
          const items = res?.data?.listProfessionalReviews?.items || [];
          allReviews.push(...items);
          nextToken = res?.data?.listProfessionalReviews?.nextToken || null;
        } while (nextToken);
      } catch (err) {
        console.warn(
          "Unable to load professional reviews; review counts will be 0.",
          err,
        );
      }

      const assessmentReviewedIds = new Set(
        allReviews
          .filter((r) => r?.ReviewType === "assessment" && r?.ReviewedId)
          .map((r) => r.ReviewedId),
      );
      const planReviewedIds = new Set(
        allReviews
          .filter((r) => r?.ReviewType === "health plan" && r?.ReviewedId)
          .map((r) => r.ReviewedId),
      );

      const [assessmentsRes, createPlansRes] = await Promise.all([
        client.graphql({
          query: listAssessmentAnswers,
          variables: {
            filter: {
              or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
              isActive: { eq: false },
            },
            limit: 1000,
          },
        }),
        client.graphql({
          query: listCreatePlans,
          variables: {
            filter: {
              userId: { eq: clientId },
            },
            limit: 1000,
          },
        }),
      ]);

      const assessments =
        assessmentsRes?.data?.listAssessmentAnswers?.items || [];
      const createPlans = createPlansRes?.data?.listCreatePlans?.items || [];

      const allowedByWindow = (ms) =>
        isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals);

      const assessmentsInWindow = assessments.filter((a) =>
        allowedByWindow(getAssessmentLikeMs(a)),
      );
      const createPlansInWindow = createPlans.filter((p) =>
        allowedByWindow(toMs(p?.createdAt)),
      );

      const assessmentsReviewed = assessmentsInWindow.filter((a) =>
        assessmentReviewedIds.has(a?.id),
      ).length;
      const plansReviewed = createPlansInWindow.filter((p) =>
        planReviewedIds.has(p?.id),
      ).length;

      setConnectedReviewStats({ assessmentsReviewed, plansReviewed });
    } catch (err) {
      console.error("Failed to fetch review stats for client:", err);
      setConnectedReviewStats({ assessmentsReviewed: 0, plansReviewed: 0 });
    }
  };
  const fetchActionStats = async (clientId) => {
    try {
      const { data } = await client.graphql({
        query: listTrackPlans,
        variables: {
          filter: {
            or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
          },
          limit: 1000,
        },
      });

      const range = buildSelectedDateRange();
      const intervals = getLinkIntervalsForClient(clientId, null);

      const itemsRaw = data?.listTrackPlans?.items || [];
      const items = itemsRaw.filter((p) => {
        const ms = toMs(p?.createdAt);
        return (
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals)
        );
      });

      let inProgress = 0;
      let discontinued = 0;
      let successful = 0;

      for (const item of items) {
        if (item.Discontinued) {
          discontinued++;
        } else if (item.Successful) {
          successful++;
        } else {
          inProgress++;
        }
      }

      setActionStats({ inProgress, discontinued, successful });
    } catch (err) {
      console.error("Failed to fetch action stats:", err);
      setActionStats({ inProgress: 0, discontinued: 0, successful: 0 });
    }
  };
  const fetchConnectionRequestStats = async () => {
    try {
      const attrs = await fetchUserAttributes();
      const userEmail = attrs.email;

      // Fetch only pending
      const pendingRes = await client.graphql({
        query: listConnectionRequests,
        variables: {
          filter: {
            targetEmail: { eq: userEmail },
            status: { eq: "pending" },
          },
        },
      });

      const totalRes = await client.graphql({
        query: listConnectionRequests,
        variables: {
          filter: {
            targetEmail: { eq: userEmail },
          },
        },
      });

      setPendingRequests(
        pendingRes?.data?.listConnectionRequests?.items?.length || 0,
      );
      setTotalRequests(
        totalRes?.data?.listConnectionRequests?.items?.length || 0,
      );
    } catch (err) {
      console.error("Error fetching connection requests:", err);
      setPendingRequests(0);
      setTotalRequests(0);
    }
  };

  const fetchClients = async () => {
    try {
      const attrs = await fetchUserAttributes();
      const userId = attrs.sub;
      const { data } = await client.graphql({ query: listUserLinks });
      const links = data?.listUserLinks?.items || [];

      const myLinks = links.filter((link) => link.professionalId === userId);

      // Only consider currently-linked clients for warnings + active client lists.
      const activeLinks = myLinks.filter((link) => !link.unlinkedAt);
      const activeClientIds = new Set(
        activeLinks.map((link) => link.clientId).filter(Boolean),
      );
      setActiveLinkedClientCount(activeClientIds.size);

      const hasHiddenClients = activeLinks.some((link) => !link.clientViewable);
      setShowClientWarning(hasHiddenClients);

      // Professionals may only view clients while their connection is active.
      const visibleLinks = activeLinks.filter(
        (link) => link.clientViewable === true,
      );
      const reportLinks = myLinks.filter(
        (link) => link.clientViewable === true,
      );

      if (
        connectedClient?.clientId &&
        !visibleLinks.some((link) => link.clientId === connectedClient.clientId)
      ) {
        setConnectedClient(null);
      }

      // Keep all viewable link intervals for historical report filtering.
      const intervalsByClientId = new Map();
      for (const link of reportLinks) {
        const clientId = link?.clientId;
        const start = link?.linkedAt || link?.createdAt || null;
        if (!clientId || !start) continue;
        const end = link?.unlinkedAt || null;
        const arr = intervalsByClientId.get(clientId) || [];
        arr.push({ start, end });
        intervalsByClientId.set(clientId, arr);
      }
      linkIntervalsByClientIdRef.current = intervalsByClientId;

      // De-dupe: keep the most recent link row per client for display.
      const byClientId = new Map();
      for (const link of visibleLinks) {
        const clientId = link?.clientId;
        if (!clientId) continue;
        const clientInfo = getUserInfoById(clientId) || {};
        const next = {
          id: link.id,
          clientId,
          firstName: link.clientFirstName,
          lastName: link.clientLastName,
          email: clientInfo.Email || link.clientEmail || "Unknown",
          createdAt: link.createdAt,
          linkedAt: link.linkedAt,
          unlinkedAt: link.unlinkedAt,
        };

        const nextStartMs = toMs(link?.linkedAt || link?.createdAt) ?? 0;
        const prev = byClientId.get(clientId);
        const prevStartMs = prev
          ? (toMs(prev?.linkedAt || prev?.createdAt) ?? 0)
          : -1;
        if (!prev || nextStartMs >= prevStartMs) byClientId.set(clientId, next);
      }

      const clientsWithEmail = Array.from(byClientId.values());

      const reportClientsById = new Map();
      for (const link of reportLinks) {
        const clientId = link?.clientId;
        if (!clientId) continue;
        const clientInfo = getUserInfoById(clientId) || {};
        const next = {
          id: link.id,
          clientId,
          firstName: link.clientFirstName,
          lastName: link.clientLastName,
          email: clientInfo.Email || link.clientEmail || "Unknown",
          createdAt: link.createdAt,
          linkedAt: link.linkedAt,
          unlinkedAt: link.unlinkedAt,
        };
        const nextStartMs = toMs(link?.linkedAt || link?.createdAt) ?? 0;
        const previous = reportClientsById.get(clientId);
        const previousStartMs = previous
          ? (toMs(previous?.linkedAt || previous?.createdAt) ?? 0)
          : -1;
        if (!previous || nextStartMs >= previousStartMs) {
          reportClientsById.set(clientId, next);
        }
      }
      const reportClientsWithEmail = Array.from(reportClientsById.values());

      setClients(clientsWithEmail);
      setFilteredClients(clientsWithEmail);
      setReportClients(reportClientsWithEmail);
      await fetchCombinedStats(reportClientsWithEmail);
      await calculateImprovementStats(reportClientsWithEmail, client);
      await fetchTopDiagnoses(reportClientsWithEmail, client);
      await fetchTopActions(reportClientsWithEmail, client);
      await fetchTopTraumas(reportClientsWithEmail, client);
    } catch (err) {
      console.error("Failed to load client links:", err);
    }
  };

  const calculateImprovementStats = async (clientsList, client) => {
    if (!client || typeof client.graphql !== "function") {
      console.error("🚨 client is not valid!", client);
    }

    try {
      const scoreFields = [
        "CMScore",
        "MHScore",
        "PHScore",
        "SHScore",
        "SoHScore",
      ];
      const improvedCount = {
        CMScore: 0,
        MHScore: 0,
        PHScore: 0,
        SHScore: 0,
        SoHScore: 0,
        OSScore: 0,
      };

      const uniqueClientIds = new Set();
      const uniqueClientsList = [];
      for (const user of clientsList || []) {
        const id = user?.clientId;
        if (!id || uniqueClientIds.has(id)) continue;
        uniqueClientIds.add(id);
        uniqueClientsList.push(user);
      }

      let comparableClientsCount = 0;

      const range = buildSelectedDateRange();

      const getAssessmentMs = (item) => getAssessmentLikeMs(item);

      const toNumber = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
      };

      for (const user of uniqueClientsList) {
        if (!user?.clientId) continue;
        const intervals = getLinkIntervalsForClient(user.clientId, user);
        const assessmentsRaw = await listAllItems({
          graphqlClient: client,
          query: listAssessmentScores,
          listKey: "listAssessmentScores",
          authMode: "userPool",
          variables: {
            filter: {
              and: [
                {
                  or: [
                    { owner: { eq: user.clientId } },
                    { userId: { eq: user.clientId } },
                  ],
                },
                {
                  or: [
                    { CompletedDate: { attributeExists: true } },
                    { isActive: { eq: false } },
                  ],
                },
              ],
            },
          },
        });

        const assessments = assessmentsRaw.filter((a) => {
          const ms = getAssessmentMs(a);
          return (
            isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals)
          );
        });

        if (assessments.length >= 2) {
          comparableClientsCount += 1;

          assessments.sort((a, b) => {
            const tA = getAssessmentMs(a) ?? 0;
            const tB = getAssessmentMs(b) ?? 0;
            if (tA !== tB) return tA - tB;
            const uA = new Date(a?.updatedAt).getTime();
            const uB = new Date(b?.updatedAt).getTime();
            if (!Number.isNaN(uA) && !Number.isNaN(uB) && uA !== uB)
              return uA - uB;
            const cA = new Date(a?.createdAt).getTime();
            const cB = new Date(b?.createdAt).getTime();
            if (!Number.isNaN(cA) && !Number.isNaN(cB) && cA !== cB)
              return cA - cB;
            return String(a?.id || "").localeCompare(String(b?.id || ""));
          });
          const secondLast = assessments[assessments.length - 2];
          const last = assessments[assessments.length - 1];

          for (const field of scoreFields) {
            const oldScore = toNumber(secondLast?.[field]) ?? 0;
            const newScore = toNumber(last?.[field]) ?? 0;
            if (oldScore - newScore >= 2) {
              improvedCount[field]++;
            }
          }

          const improvedCountForClient = scoreFields.filter((field) => {
            const oldScore = toNumber(secondLast?.[field]) ?? 0;
            const newScore = toNumber(last?.[field]) ?? 0;
            return oldScore - newScore >= 2;
          }).length;

          if (improvedCountForClient >= 3) {
            improvedCount.OSScore++;
          }
        }
      }

      const calculatePercent = (count) =>
        comparableClientsCount === 0
          ? "--"
          : Math.round((count / comparableClientsCount) * 100) + "%";

      setImprovementStats({
        CM: calculatePercent(improvedCount.CMScore),
        MH: calculatePercent(improvedCount.MHScore),
        PH: calculatePercent(improvedCount.PHScore),
        SH: calculatePercent(improvedCount.SHScore),
        SoH: calculatePercent(improvedCount.SoHScore),
        OS: calculatePercent(improvedCount.OSScore),
      });
    } catch (err) {
      console.error("Error calculating improvement stats:", err);
      setImprovementStats({
        CM: "--",
        MH: "--",
        PH: "--",
        SH: "--",
        SoH: "--",
        OS: "--",
      });
    }
  };

  const fetchCombinedStats = async (clientsList) => {
    try {
      let totalAssessments = 0;
      let unduplicatedAssessments = 0;
      let totalInProgress = 0;
      let totalDiscontinued = 0;
      let totalSuccessful = 0;
      let assessmentsReviewed = 0;
      let plansReviewed = 0;

      const range = buildSelectedDateRange();

      const allReviews = [];
      let nextToken = null;
      do {
        const res = await client.graphql({
          query: listProfessionalReviews,
          variables: { limit: 1000, nextToken },
        });
        const items = res?.data?.listProfessionalReviews?.items || [];
        allReviews.push(...items);
        nextToken = res?.data?.listProfessionalReviews?.nextToken || null;
      } while (nextToken);

      const assessmentReviewedIds = new Set(
        allReviews
          .filter((r) => r?.ReviewType === "assessment" && r?.ReviewedId)
          .map((r) => r.ReviewedId),
      );
      const planReviewedIds = new Set(
        allReviews
          .filter((r) => r?.ReviewType === "health plan" && r?.ReviewedId)
          .map((r) => r.ReviewedId),
      );

      for (const c of clientsList) {
        const clientId = c?.clientId;
        if (!clientId) continue;

        const intervals = getLinkIntervalsForClient(clientId, c);

        const [assessmentsRaw, plansRaw, createPlansRaw] = await Promise.all([
          listAllItems({
            graphqlClient: client,
            query: listAssessmentAnswers,
            listKey: "listAssessmentAnswers",
            authMode: "userPool",
            variables: {
              filter: {
                and: [
                  {
                    or: [
                      { owner: { eq: clientId } },
                      { userId: { eq: clientId } },
                    ],
                  },
                  {
                    or: [
                      { CompletedDate: { attributeExists: true } },
                      { isActive: { eq: false } },
                    ],
                  },
                ],
              },
            },
          }),
          listAllItems({
            graphqlClient: client,
            query: listTrackPlans,
            listKey: "listTrackPlans",
            authMode: "userPool",
            variables: {
              filter: {
                or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
              },
            },
          }),
          listAllItems({
            graphqlClient: client,
            query: listCreatePlans,
            listKey: "listCreatePlans",
            authMode: "userPool",
            variables: {
              filter: {
                userId: { eq: clientId },
              },
            },
          }),
        ]);

        const allowedByWindow = (ms) =>
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals);

        const assessments = assessmentsRaw.filter((a) =>
          allowedByWindow(getAssessmentLikeMs(a)),
        );
        const plans = plansRaw.filter((p) =>
          allowedByWindow(toMs(p?.createdAt)),
        );
        const createPlans = createPlansRaw.filter((p) =>
          allowedByWindow(toMs(p?.createdAt)),
        );

        totalAssessments += assessments.length;
        if (assessments.length > 0) unduplicatedAssessments += 1;
        assessmentsReviewed += assessments.filter((a) =>
          assessmentReviewedIds.has(a?.id),
        ).length;
        plansReviewed += createPlans.filter((p) =>
          planReviewedIds.has(p?.id),
        ).length;

        for (const plan of plans) {
          if (plan.Discontinued) totalDiscontinued++;
          else if (plan.Successful) totalSuccessful++;
          else totalInProgress++;
        }
      }

      setCombinedStats({
        totalAssessments,
        unduplicatedAssessments,
        totalInProgress,
        totalDiscontinued,
        totalSuccessful,
        assessmentsReviewed,
        plansReviewed,
      });
    } catch (err) {
      console.error("Failed to fetch combined client stats:", err);
      if (Array.isArray(err?.errors) && err.errors.length) {
        console.error(
          "Combined stats GraphQL errors:",
          err.errors.map((e) => e?.message || e),
        );
      }
      setCombinedStats({
        totalAssessments: 0,
        unduplicatedAssessments: 0,
        totalInProgress: 0,
        totalDiscontinued: 0,
        totalSuccessful: 0,
        assessmentsReviewed: 0,
        plansReviewed: 0,
      });
    }
  };

  const fetchAssessmentStats = async (clientId) => {
    try {
      const { data } = await client.graphql({
        query: listAssessmentAnswers,
        variables: {
          filter: {
            and: [
              {
                or: [{ owner: { eq: clientId } }, { userId: { eq: clientId } }],
              },
              {
                or: [
                  { CompletedDate: { attributeExists: true } },
                  { isActive: { eq: false } },
                ],
              },
            ],
          },
          limit: 1000,
        },
        authMode: "userPool",
      });

      const range = buildSelectedDateRange();
      const intervals = getLinkIntervalsForClient(clientId, null);

      const itemsRaw = data?.listAssessmentAnswers?.items || [];
      const completed = itemsRaw.filter((a) => {
        const ms = getAssessmentLikeMs(a);
        return (
          isMsWithinRange(ms, range) && isMsWithinAnyInterval(ms, intervals)
        );
      }).length;
      setCompletedAssessments(completed);
    } catch (err) {
      console.error("Failed to fetch assessments:", err);
      setCompletedAssessments(0);
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 20,
          minHeight: "100vh",
          width: "100vw",
        }}
      >
        {connectedClient && (
          <div
            style={{
              width: "90%",
              maxWidth: "1000px",
              padding: "12px 20px",
              backgroundColor: "#d9eaff",
              border: "2px solid #0067B3",
              borderRadius: 8,
              marginBottom: 10,
              textAlign: "center",
              fontWeight: "bold",
              color: "#0067B3",
            }}
          >
            Currently Viewing: {connectedClient.firstName}{" "}
            {connectedClient.lastName}
            <button
              onClick={() => setConnectedClient(null)}
              style={{
                marginLeft: 16,
                padding: "6px 12px",
                backgroundColor: "#8C1456",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              View All Clients
            </button>
          </div>
        )}
        {/* LEFT STATS BOX */}
        <div style={{ ...containerStyle2, marginBottom: 24 }}>
          <h3 style={titleStyle}>General</h3>
          <div style={statsRow}>
            <div style={statLine}>
              Plans Paid For
              <div style={highlightNumber}>{plansPaidFor}</div>
            </div>
            <div style={statLine}>
              Plans Remaining
              <div style={highlightNumber}>
                {Math.max(0, plansPaidFor - activeLinkedClientCount)}
              </div>
            </div>
            <div style={statLine}>
              Active Clients
              <div style={highlightNumber}>
                {connectedClient ? 1 : clients.length}
              </div>
            </div>
            <div style={statLine}>
              Pending Clients
              <div style={highlightNumber}>{pendingRequests}</div>
            </div>
          </div>
        </div>

        <div style={containerStyle2}>
          <h3 style={titleStyle}>Top 5 Stats</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
              gap: 10,
              alignItems: "start",
            }}
          >
            {TOP5_SECTIONS.map(({ key, label }, idx) => {
              const tile = (
                <div key={key} style={{ minWidth: 0, padding: "16px 0" }}>
                  <button
                    onClick={() => toggleTop5Section(key)}
                    style={{
                      cursor: "pointer",
                      padding: "12px 12px",
                      borderRadius: 12,
                      border: openTop5Sections?.[key]
                        ? "2px solid #0067B3"
                        : "1px solid #ccc",
                      backgroundColor: openTop5Sections?.[key]
                        ? "#d9eaff"
                        : "#fff",
                      color: "#0067B3",
                      fontWeight: 600,
                      width: "100%",
                      fontSize: 14,
                      textAlign: "center",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    }}
                  >
                    {label}
                  </button>

                  {openTop5Sections?.[key] && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        marginTop: 10,
                        textAlign: "left",
                      }}
                    >
                      {(top5Stats?.[key] || []).length === 0 ? (
                        <div style={{ ...statLine, alignItems: "flex-start" }}>
                          No Data Available
                        </div>
                      ) : (
                        (top5Stats?.[key] || []).map((item) => (
                          <div
                            key={item.label}
                            style={{
                              fontSize: 14,
                              color: "#0067B3",
                              fontWeight: "normal",
                              padding: "2px 0",
                            }}
                          >
                            {item.label} ({formatTop5Count(key, item.count)})
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );

              // Add divider after OUTSIDE_RANGE_SOH for bottom row
              if (!isMobile && (key === "OUTSIDE_RANGE_SOH" || idx === 7)) {
                return [
                  tile,
                  <div
                    key={`${key}-divider`}
                    style={{
                      gridColumn: "1 / -1",
                      height: 1,
                      backgroundColor: "#ccc",
                      width: "100%",
                      justifySelf: "stretch",
                    }}
                  />,
                ];
              }
              // Add divider after the first row (after idx === 3)
              if (!isMobile && idx === 3) {
                return [
                  tile,
                  <div
                    key={`row1-divider`}
                    style={{
                      gridColumn: "1 / -1",
                      height: 1,
                      backgroundColor: "#ccc",
                      width: "100%",
                      justifySelf: "stretch",
                    }}
                  />,
                ];
              }
              return tile;
            })}
          </div>
          <div
            style={{
              marginTop: 24,
              fontSize: 12,
              color: "#0067B3",
              textAlign: "center",
            }}
          >
            Note: This section uses each client's most recent assessment and
            plan within the selected date range (unduplicated).
          </div>
        </div>

        {/* MIDDLE & RIGHT STATS BOX */}
        <div style={{ ...statsContainerResponsive, marginBottom: 5 }}>
          {/* MIDDLE STATS BOX */}
          <div style={sideBoxStyle}>
            <h3 style={titleStyle}>
              {connectedClient
                ? `Stats for: ${connectedClient.firstName} ${connectedClient.lastName}`
                : "Plan and Progress Stats (All Clients)"}
            </h3>
            <div style={statsRowAligned}>
              <div style={statLineAligned}>
                Active Plans
                <div style={highlightNumberAligned}>
                  {connectedClient
                    ? actionStats.inProgress
                    : combinedStats.totalInProgress}
                </div>
              </div>
              <div style={statLineAligned}>
                Recommendations Made
                <div style={highlightNumberAligned}>
                  {recommendationStats.sent}
                </div>
              </div>
              <div style={statLineAligned}>
                Successful Actions
                <div style={highlightNumberAligned}>
                  {connectedClient
                    ? actionStats.successful
                    : combinedStats.totalSuccessful}
                </div>
              </div>
              <div style={statLineAligned}>
                Discontinued Actions
                <div style={highlightNumberAligned}>
                  {connectedClient
                    ? actionStats.discontinued
                    : combinedStats.totalDiscontinued}
                </div>
              </div>
              <div style={statLineAligned}>
                Assessments Reviewed
                <div style={highlightNumberAligned}>
                  {connectedClient
                    ? connectedReviewStats.assessmentsReviewed
                    : combinedStats.assessmentsReviewed}
                </div>
              </div>
              <div style={statLineAligned}>
                Plans Reviewed
                <div style={highlightNumberAligned}>
                  {connectedClient
                    ? connectedReviewStats.plansReviewed
                    : combinedStats.plansReviewed}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT STATS BOX */}
          <div style={sideBoxStyle}>
            <h3 style={titleStyle}>
              {connectedClient
                ? "Client Improvement"
                : "Clients Improvement (All Clients)"}
            </h3>
            <div style={statsRowAligned}>
              <div style={statLineAligned}>
                Assessments Completed
                <div style={highlightNumberAligned}>
                  {connectedClient
                    ? completedAssessments
                    : combinedStats.totalAssessments}
                </div>
              </div>
              <div style={statLineAligned}>
                Unduplicated Assessments
                <div style={highlightNumberAligned}>
                  {connectedClient
                    ? Number(completedAssessments) > 0
                      ? 1
                      : 0
                    : combinedStats.unduplicatedAssessments}
                </div>
              </div>
              <div style={statLineAligned}>
                Coping Improvement
                <div style={highlightNumberAligned}>{improvementStats.CM}</div>
              </div>
              <div style={statLineAligned}>
                Mental Improvement
                <div style={highlightNumberAligned}>{improvementStats.MH}</div>
              </div>
              <div style={statLineAligned}>
                Physical Improvement
                <div style={highlightNumberAligned}>{improvementStats.PH}</div>
              </div>
              <div style={statLineAligned}>
                Spiritual Improvement
                <div style={highlightNumberAligned}>{improvementStats.SH}</div>
              </div>
              <div style={statLineAligned}>
                Social Improvement
                <div style={highlightNumberAligned}>{improvementStats.SoH}</div>
              </div>
              <div style={statLineAligned}>
                Overall Score
                <div style={highlightNumberAligned}>{improvementStats.OS}</div>
              </div>
            </div>
          </div>
        </div>

        {/* CLIENTS LIST */}
        <div style={{ ...containerStyle2, marginTop: 40 }}>
          <h3 style={titleStyle}>Social Determinants of Health</h3>
          <div
            style={{
              ...statsRowAligned,
              gridTemplateColumns: isMobile
                ? "repeat(2, minmax(170px, 1fr))"
                : "repeat(4, minmax(170px, 1fr))",
              justifyItems: "center",
            }}
          >
            {sdoHPieOrder.map((field, idx) => {
              const label = sdoHDisplayNames[field] || field;
              const fieldStats = sdoHStats?.byField?.[field] || {
                total: 0,
                counts: {},
              };
              const segments = buildPieSegments(fieldStats.counts, 6);

              const tile = (
                <div
                  key={field}
                  style={{ ...statLineAligned, alignItems: "stretch" }}
                >
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#0067B3",
                      textAlign: "center",
                    }}
                  >
                    {label}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    <div
                      aria-label={`${label} distribution pie chart`}
                      style={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        ...pieStyleFromSegments(segments),
                      }}
                    />

                    {segments.length === 0 ? (
                      <div
                        style={{
                          color: "#0067B3",
                          fontSize: 14,
                          textAlign: "center",
                        }}
                      >
                        No Data Available
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "inline-block",
                          maxWidth: 230,
                          margin: "0 auto",
                          textAlign: "left",
                        }}
                      >
                        {segments.map((s) => (
                          <div
                            key={s.label}
                            style={{
                              display: "flex",
                              justifyContent: "flex-start",
                              gap: 10,
                              fontSize: 13,
                              color: "#0067B3",
                              marginTop: 6,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: 2,
                                  backgroundColor: s.color,
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {s.label}
                              </span>
                            </div>
                            <span style={{ fontWeight: 600 }}>
                              {s.percent.toFixed(0)}%
                            </span>
                          </div>
                        ))}
                        <div
                          style={{
                            marginTop: 10,
                            fontSize: 12,
                            color: "#0067B3",
                            textAlign: "center",
                          }}
                        >
                          n = {fieldStats.total}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );

              const divider =
                !isMobile && idx === 3 ? (
                  <div
                    key={`${field}-divider`}
                    style={{
                      gridColumn: "1 / -1",
                      height: 1,
                      backgroundColor: "#ccc",
                      width: "100%",
                      justifySelf: "stretch",
                      marginTop: 12,
                      marginBottom: 12,
                    }}
                  />
                ) : null;

              return [tile, divider];
            })}
          </div>
        </div>

        <div style={{ ...containerStyle2, marginTop: 40 }}>
          {showClientWarning && (
            <div
              style={{
                backgroundColor: "#fff3cd",
                color: "#856404",
                padding: "10px",
                border: "1px solid #ffeeba",
                borderRadius: "5px",
                marginBottom: "10px",
              }}
            >
              Some clients are temporarily inactive because the payment for your
              subscription was not successful. Please update your payment method
              to make all clients active.
            </div>
          )}

          <h3 style={titleStyle}>Your Clients</h3>

          {/* FILTERS */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row", // stack on mobile
              gap: "10px", // space between inputs
              marginBottom: "20px",
              alignItems: isMobile ? "stretch" : "center", // stretch inputs on mobile
            }}
          >
            {/* Text box filter */}
            <input
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: "1",
                minWidth: isMobile ? "100%" : 240,
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
              }}
            />

            {/* Date filters */}
            <input
              type="date"
              value={pendingStartDate}
              onChange={(e) => setPendingStartDate(e.target.value)}
              style={{
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            />
            <input
              type="date"
              value={pendingEndDate}
              onChange={(e) => setPendingEndDate(e.target.value)}
              style={{
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            />

            <button
              onClick={() => {
                setStartDate(pendingStartDate);
                setEndDate(pendingEndDate);
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "6px",
                border: "none",
                backgroundColor: "#0067B3",
                color: "#fff",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              Apply Filters
            </button>
          </div>

          {/* CLIENTS */}
          <div style={linksSectionStyle}>
            {filteredClients.length === 0 ? (
              <p style={emptyStyle}>No clients found.</p>
            ) : (
              filteredClients.map((client) => (
                <div key={client.id} style={linkBoxStyle}>
                  <div
                    style={{
                      ...clientGridStyle,
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(3, minmax(0, 1fr)) auto",
                    }}
                  >
                    <div style={userColumnStyle}>
                      <strong>Email</strong>
                      <div>{client.email}</div>
                    </div>
                    <div style={userColumnStyle}>
                      <strong>Name</strong>
                      <div>
                        {client.firstName} {client.lastName}
                      </div>
                    </div>
                    <div style={userColumnStyle}>
                      <strong>Linked Since</strong>
                      <div>
                        {client.linkedAt || client.createdAt
                          ? new Date(
                              client.linkedAt || client.createdAt,
                            ).toLocaleDateString()
                          : "--"}
                      </div>
                    </div>
                    <div
                      style={{
                        ...clientActionStyle,
                        justifySelf: isMobile ? "stretch" : "end",
                      }}
                    >
                      {(() => {
                        const isSelected =
                          connectedClient?.clientId === client.clientId;
                        return (
                          <button
                            style={{
                              ...buttonStyle,
                              backgroundColor: isSelected
                                ? "#8C1456"
                                : "#0067B3",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              isSelected
                                ? setConnectedClient(null)
                                : setConnectedClient(client)
                            }
                          >
                            {isSelected ? "Stop Viewing" : "View"}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ===== STYLES =====

const titleStyle = {
  fontSize: 20,
  fontWeight: "bold",
  marginBottom: 16,
  color: "#0067B3",
  alignItems: "center",
};

const linksSectionStyle = {
  marginTop: 20,
};

const linkBoxStyle = {
  backgroundColor: "#f9f9f9",
  padding: 16,
  borderRadius: 10,
  marginBottom: 16,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
};

const userColumnStyle = {
  flex: 1,
  minWidth: 0,
};

const clientGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr)) auto",
  gap: 16,
  alignItems: "center",
  width: "100%",
};

const clientActionStyle = {
  justifySelf: "end",
};

const emptyStyle = {
  fontStyle: "italic",
  color: "#333",
};

const statsBoxStyle = {
  flexDirection: "column",
  alignItems: "center",
  width: "1000px",
  margin: "1px auto",
  padding: 24,
  borderRadius: 12,
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  display: "block",
};

const statsRow = {
  display: "flex",
  flexDirection: "row", // side by side
  justifyContent: "center",
  gap: 24, // spacing between each stat
  flexWrap: "wrap", // wrap if screen is too small
};

// Used specifically for the two side-by-side dashboard stat boxes to keep
// stat blocks aligned regardless of label length and count.
const statsRowAligned = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 24,
  alignItems: "start",
};

const statLine = {
  display: "flex",
  flexDirection: "column", // label on top, number below
  alignItems: "center",
  fontSize: 14,
  color: "#0067B3",
};

const statLineAligned = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  fontSize: 14,
  color: "#0067B3",
  width: "100%",
};

const highlightNumber = {
  display: "block",
  textAlign: "center",
  borderRadius: 12,
  color: "#0067B3",
  fontSize: 18,
  fontWeight: 600,
  padding: "6px 12px",
  marginTop: 6,
  marginBottom: 6,
  minWidth: 120,
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};

const highlightNumberAligned = {
  display: "block",
  textAlign: "center",
  borderRadius: 12,
  color: "#0067B3",
  fontSize: 18,
  fontWeight: 600,
  padding: "6px 12px",
  marginTop: 6,
  marginBottom: 6,
  width: "100%",
  minWidth: "unset",
  boxSizing: "border-box",
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};
const buttonStyle = {
  backgroundColor: "#0067B3",
  color: "white",
  fontWeight: "bold",
  padding: "8px 16px",
  borderRadius: 6,
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};
